import { useHistory } from "react-router-dom";
import moment from "moment";
import { notification } from "antd";
import { useTripsCtx } from "../context/TripDetailsContext";
import { ITripDetails } from "../components/ServiceUsers/Interfaces/ITripDetails";
import { TripType, TripTypesButton } from "../types/TripTypes";
import { RevealTripStatus } from "../types/ApplicationTypes";
import {
  deleteFavoriteTrip,
  updateFavoriteTrip,
  tripStatusChange,
  getTripByRequestAssignmentId,
} from "../services/RegistrationService";
import {
  TriggerEventId,
  EmailTripBookingModel,
} from "../types/EmailNotificationType";
import EmailTripBooking from "../helpers/EmailTripBooking";
import { usePlanDetailsCtx } from "../context/PlanDetailsContext";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import { useDashboardCtx } from "../context/DashboardContext";
import { Tabs } from "../types/TripPaginationFilterTypes";

export interface RebookTripOptions {
  selectedLeg?: ITripDetails;
  allLegs?: ITripDetails[];
  areTripLegsMissing?: boolean;
}

export const useTripActions = () => {
  const {
    isRebookCtx,
    rebookTripCtx,
    wandaOrgId,
    upcomingTripDetailsCtx,
    pastTripDetailsCtx,
    favoriteTripDetailsCtx,
  } = useTripsCtx();

  const [upcomingTripDetails] = upcomingTripDetailsCtx;
  const [pastTripDetails] = pastTripDetailsCtx;
  const [favoriteTripDetails, setFavoriteTripDetails] = favoriteTripDetailsCtx;

  const [, setIsRebook] = isRebookCtx;
  const [, setRebookTrip] = rebookTripCtx;

  const history = useHistory();
  const {
    currentPlan,
    tripDetailsInfo,
    tripsAfterSubscriptionDateCtx,
  } = usePlanDetailsCtx();
  const [, setTripsAfterSubscriptionDate] = tripsAfterSubscriptionDateCtx;

  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const { forceRefetchCtx } = useDashboardCtx();
  const [forceRefetch, setForceRefetch] = forceRefetchCtx;

  const compareAddresses = (addressA: ITripDetails, addressB: ITripDetails) => {
    return (
      addressA.pickUpAddress1 === addressB.dropOffAddress1 &&
      addressA.pickUpCity === addressB.dropOffCity &&
      addressA.pickUpState === addressB.dropOffState &&
      addressA.pickUpZipCode === addressB.dropOffZipCode
    );
  };

  const addressesMatch = (legA: ITripDetails, legB: ITripDetails) => {
    return (
      compareAddresses(legA, legB) &&
      compareAddresses(
        {
          ...legA,
          pickUpAddress1: legA.dropOffAddress1,
          pickUpCity: legA.dropOffCity,
          pickUpState: legA.dropOffState,
          pickUpZipCode: legA.dropOffZipCode,
        },
        {
          ...legB,
          dropOffAddress1: legB.pickUpAddress1,
          dropOffCity: legB.pickUpCity,
          dropOffState: legB.pickUpState,
          dropOffZipCode: legB.pickUpZipCode,
        }
      )
    );
  };

  const evaluateTrip = (tripLegs: ITripDetails[], leg: number) => {
    const [firstLeg, secondLeg, thirdLeg] = tripLegs;

    if (leg === 1) {
      return TripTypesButton.Inital;
    }

    if (leg === 2) {
      return addressesMatch(firstLeg, secondLeg)
        ? TripTypesButton.ReturnTrip
        : TripTypesButton.AddStop;
    }

    if (leg === 3) {
      return addressesMatch(secondLeg, thirdLeg) ||
        compareAddresses(firstLeg, thirdLeg)
        ? TripTypesButton.ReturnTrip
        : TripTypesButton.AddStop;
    }
  };

  async function rebookTrip(options: RebookTripOptions) {
    try {
      const { selectedLeg, allLegs, areTripLegsMissing } = options;
      const personId = selectedLeg?.personId ?? allLegs?.[0]?.personId;
      let tripLegsToRebook: ITripDetails[] = [];
      const sortTripLegs = (a: ITripDetails, b: ITripDetails) =>
        a.bookingId.localeCompare(b.bookingId);

      if (allLegs && allLegs.length) {
        tripLegsToRebook = allLegs.sort(sortTripLegs);
      } else if (selectedLeg && areTripLegsMissing) {
        const apiResponse = await getTripByRequestAssignmentId({
          requestAssignmentId: selectedLeg.requestAssignmentId,
          personId: selectedLeg.personId,
          favoriteId: selectedLeg.favoriteId,
          tripDate: selectedLeg.tripDate,
        });
        tripLegsToRebook = apiResponse.personTrips.sort(sortTripLegs);
      } else if (selectedLeg) {
        const totalTrips = [...upcomingTripDetails, ...pastTripDetails];
        tripLegsToRebook = totalTrips
          .filter(
            (trip: ITripDetails) =>
              trip.requestAssignmentId === selectedLeg.requestAssignmentId
          )
          .sort(sortTripLegs);
      }

      const serviceUser = serviceData?.find(
        (data: { personId: number }) => data.personId === personId
      );

      const tripCreationData = tripLegsToRebook.map(
        (trip: ITripDetails, index: number) => {
          return {
            id: index + 1,
            scheduleTime: trip.isWillCall
              ? ""
              : moment(
                  trip.appointmentTime === null
                    ? trip.requestPickTime
                    : trip.appointmentTime
                ).format("hh:mm A"),
            tripType: evaluateTrip(tripLegsToRebook, index + 1),
            tripSelectedTypeDefault:
              trip.appointmentTime === null
                ? TripType.LeaveAt
                : TripType.ArrivedBy,
            pickTime: trip.requestPickTime,
            pickupAddressDefault: {
              address1: trip.pickUpAddress1,
              address2: trip.pickUpAddress2,
              city: trip.pickUpCity,
              state: trip.pickUpState,
              zip: trip.pickUpZipCode.toString(),
              county: trip.pickUpCounty,
              isDriverInStructionEnabled: trip.pickupNotes && true,
              driverInstruction: trip.pickupNotes,
              latitude: trip.pickLatitude,
              longitude: trip.pickLongitude,
            },
            dropOffAddressDefault: {
              address1: trip.dropOffAddress1,
              address2: trip.dropOffAddress2,
              city: trip.dropOffCity,
              state: trip.dropOffState,
              zip: trip.dropOffZipCode.toString(),
              county: trip.dropOffCounty,
              isDriverInStructionEnabled: trip.dropNotes && true,
              driverInstruction: trip.dropNotes,
              latitude: trip.dropLatitude,
              longitude: trip.dropLongitude,
            },
            firstName: trip.serviceUser.split(" ")[0],
            isWillCall: tripLegsToRebook.length === 1 ? false : trip.isWillCall,
            wheelChairTravel:
              serviceUser?.wandaAccommodations?.wheelChairTravel,
          };
        }
      );

      setIsRebook(true);
      setRebookTrip(tripCreationData);

      history.push(
        `/DashBoard/ScheduleService/ScheduleServiceCalender/${caregiverSummaryInfo.personId}/${tripLegsToRebook[0].personId}`
      );
    } catch (error) {
      notification.error({
        message: "Failed to rebook trip",
      });
    }
  }

  const cancelTrip = async (trip: ITripDetails, isDownGrade?: boolean) => {
    try {
      let tripsToCancel = {
        customerId: trip.customerId,
        date: trip.tripDate.split("T")[0],
        mfTripStatusId: RevealTripStatus.MemberCancel,
        personId: trip.personId,
        reservationId: Number(trip.bookingId),
        requestAssignmentId: trip.requestAssignmentId,
        redemptionId: trip.redemptionId,
      };
      await tripStatusChange(tripsToCancel, Number(trip.personId));
      currentPlan === "Plus" &&
        setTripsAfterSubscriptionDate(
          (tripsAfterSubscriptionDate: ITripDetails[]) =>
            tripsAfterSubscriptionDate.filter(
              (tripAfterSubscriptionDate: ITripDetails) =>
                tripAfterSubscriptionDate.requestAssignmentId !==
                trip.requestAssignmentId
            )
        );

      setForceRefetch((prev: { [tab: string]: boolean }) => ({
        ...prev,
        [Tabs.UPCOMING]: !prev[Tabs.UPCOMING],
      }));

      if (!isDownGrade) {
        notification.success({
          message: "Success",
          description: "Successfully canceled the trip(s)",
        });
        let emailData: EmailTripBookingModel = {
          eventId: TriggerEventId.BookingCancelled,
          careGiverPersonId: caregiverSummaryInfo.personId,
          careGiverName: caregiverSummaryInfo.firstName,
          organizationId: wandaOrgId.privatePayId,
          bookingId: trip.bookingId,
          requestAssignmentId: trip.requestAssignmentId,
          serviceUserPersonId: trip.personId,
          tripDate: trip.tripDate,
          sourceSystemId: trip.sourceSystemId,
        };
        await EmailTripBooking(
          emailData,
          tripDetailsInfo.wandaCustomerCareNumber
        );
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Cancel Trips.",
      });
    }
  };

  const removeFavoriteTrip = async (trips: ITripDetails[]) => {
    try {
      let bookingIds = trips.map((trip) => Number(trip.bookingId));
      let tripsToRemove = {
        personId: trips[0].personId,
        name: trips[0].favoriteName,
        bookingId: bookingIds,
        requestAssignmentId: trips[0].requestAssignmentId,
        favoriteId: trips[0].favoriteId,
      };
      await deleteFavoriteTrip(tripsToRemove);
      notification.success({
        message: "Success",
        description: "Successfully removed the trip(s)",
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Remove Favorite Trip",
      });
    }
  };

  const renameFavoriteTrip = async (
    updatedName: string,
    favTrip: ITripDetails[]
  ) => {
    try {
      let bookingIds = favTrip.map((leg) => Number(leg.bookingId));
      let tripsToRename = {
        personId: favTrip[0].personId,
        name: updatedName,
        bookingId: bookingIds,
        requestAssignmentId: favTrip[0].requestAssignmentId,
        favoriteId: favTrip[0].favoriteId,
      };
      await updateFavoriteTrip(tripsToRename);
      setFavoriteTripDetails((favoriteTrips: ITripDetails[]) =>
        favoriteTrips.map((trip) => {
          if (trip.favoriteId === favTrip[0].favoriteId)
            return {
              ...trip,
              favoriteName: updatedName,
            };
          else return trip;
        })
      );
      notification.success({
        message: "Success",
        description: "Successfully renamed the trip(s)",
      });
    } catch (error) {
      throw error;
    }
  };

  return { rebookTrip, cancelTrip, removeFavoriteTrip, renameFavoriteTrip };
};
